import React, {
	type ReactElement,
	type ReactNode,
	type RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';

import Button from '@atlaskit/button/default/button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import type {
	DropdownMenuProps,
	CustomTriggerProps as TriggerProps,
} from '@atlaskit/dropdown-menu/types';
import ExpandIcon from '@atlaskit/icon/core/chevron-down';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type {
	AnyEntryPoint,
	EntryPointRef,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { ErrorContent } from '../error-content';
import { StatusAnnouncement } from '../status-announcement';
import { useStatusAnnouncement } from '../use-status-announcement';
import { Loading } from './loading';

type ContentState = 'loading' | 'loaded' | 'error';

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomDropdownProps = UnionOmit<
	DropdownMenuProps,
	'trigger' | 'handleOpenChange' | 'children'
>;

export type CustomTriggerProps = TriggerProps & { ref: TriggerProps['triggerRef'] };

export type DropdownTriggerProps<TEntryPoint> = {
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: RuntimePropsOfEntryPoint<TEntryPoint>;
	errorFallback?: ErrorFallback;
	fallback?: ReactNode;
	onError?: ErrorCallback;
	/**
	 * The text that renders during the loading screen.
	 */
	loadingText?: ReactNode;
	/**
	 * Announced after an announced loading state completes.
	 */
	loadingCompleteText?: ReactNode;
	/**
	 * Displayed by the default error fallback and announced when content fails to load,
	 * including with a custom error fallback.
	 */
	errorText?: ReactNode;
	trigger: string | ((triggerProps: CustomTriggerProps) => ReactElement);
} & CustomDropdownProps;

function MenuBusyState({ state }: { state: ContentState }) {
	const ref = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		const menu = ref.current?.closest('[role="menu"]');
		if (!menu) {
			return;
		}

		// Loading and error content can temporarily lack the menu's required owned roles.
		menu.setAttribute('aria-busy', String(state !== 'loaded'));
		return () => {
			menu.setAttribute('aria-busy', 'false');
		};
	}, [state]);

	return <span ref={ref} hidden />;
}

function BusyFallback({
	children,
	state,
	onStateChange,
}: {
	children: ReactNode;
	state: 'loading' | 'error';
	onStateChange: (state: ContentState) => void;
}) {
	useLayoutEffect(() => {
		onStateChange(state);
		return () => onStateChange('loaded');
	}, [onStateChange, state]);

	return <>{children}</>;
}

const FallbackComponent = ({
	fallback,
	loadingText,
	onMount,
	onUnmount,
}: {
	fallback?: ReactNode;
	loadingText?: ReactNode;
	onMount: () => void;
	onUnmount: () => void;
}) => {
	useEffect(() => {
		onMount();
		return () => onUnmount();
	}, [onMount, onUnmount]);

	return (
		<span data-testid="dropdown-trigger-loading">
			{fallback || <Loading loadingText={loadingText} />}
		</span>
	);
};

export function DropdownTrigger<TEntryPoint extends AnyEntryPoint>({
	trigger,
	entryPoint,
	entryPointParams,
	entryPointProps,
	fallback,
	errorFallback,
	loadingText = 'Loading dropdown menu...',
	loadingCompleteText = 'Loading is complete.',
	errorText = 'Something went wrong while loading the menu content.',
	onError,
	...dropdownProps
}: DropdownTriggerProps<TEntryPoint>): React.JSX.Element {
	const environmentProvider = useRelayEnvironmentProvider();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const [isOpen, setIsOpen] = useState(false);
	const [contentState, setContentState] = useState<ContentState>('loaded');
	const { statusText, announce, cancelPendingAnnouncement, resetStatus } = useStatusAnnouncement();
	const isOpenRef = useRef(false);
	const hasErrorRef = useRef(false);
	const isAccessibleLoadingEnabled = fg('platform_dst-improve-dropdown-error-experience');
	const [children, setChildren] = useState<ReactNode | null>(null);

	const handleError = useCallback<ErrorCallback>(
		(props) => {
			if (fg('platform_dst-improve-dropdown-error-experience') && isOpenRef.current) {
				hasErrorRef.current = true;
				resetStatus();
				announce({ text: errorText, delay: 100, clearAfter: 5000 });
			}
			onError?.(props);
		},
		[announce, errorText, onError, resetStatus],
	);

	const handleOpenChange = useCallback(
		({ isOpen }: { isOpen: boolean }) => {
			isOpenRef.current = isOpen;
			setIsOpen(isOpen);
			if (!isOpen && fg('platform_dst-improve-dropdown-error-experience')) {
				resetStatus();
			}
		},
		[resetStatus],
	);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const handleLoadingMount = useCallback(() => {
		if (fg('platform_dst-improve-dropdown-error-experience')) {
			announce({ text: loadingText, delay: 100 });
		}
	}, [announce, loadingText]);

	const handleLoadingUnmount = useCallback(() => {
		if (fg('platform_dst-improve-dropdown-error-experience') && !hasErrorRef.current) {
			const wasPending = cancelPendingAnnouncement();
			if (!wasPending && isOpenRef.current) {
				announce({ text: loadingCompleteText, clearAfter: 5000 });
			}
		}
	}, [announce, cancelPendingAnnouncement, loadingCompleteText]);

	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			// avoid re-loading entry-point when closing popup
			if (isOpen) {
				return;
			}

			hasErrorRef.current = false;
			setChildren(
				<InternalEntryPointContainer
					id={id}
					entryPointReference={entryPointReference}
					fallback={
						<BusyFallback state="loading" onStateChange={setContentState}>
							<FallbackComponent
								fallback={fallback}
								loadingText={loadingText}
								onMount={handleLoadingMount}
								onUnmount={handleLoadingUnmount}
							/>
						</BusyFallback>
					}
					errorFallback={(props) => (
						<BusyFallback state="error" onStateChange={setContentState}>
							{errorFallback ? (
								errorFallback(props)
							) : fg('platform_dst-improve-dropdown-error-experience') ? (
								<ErrorContent errorText={errorText} />
							) : null}
						</BusyFallback>
					)}
					runtimeProps={entryPointProps || emptyEntryPointProps}
					onError={handleError}
				/>,
			);
		},
		[
			setChildren,
			id,
			isOpen,
			entryPointProps,
			fallback,
			errorFallback,
			errorText,
			handleError,
			loadingText,
			handleLoadingMount,
			handleLoadingUnmount,
		],
	);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
	});

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps): ReactElement => {
			const mergedRef: RefObject<any> = mergeRefs([triggerRef, triggerProps.triggerRef]);

			if (typeof trigger === 'string') {
				return (
					<Button
						{...triggerProps}
						ref={mergedRef}
						isSelected={isOpen}
						iconAfter={(iconProps) => <ExpandIcon {...iconProps} size="small" />}
						testId={dropdownProps.testId && `${dropdownProps.testId}--trigger`}
						aria-label={dropdownProps.label}
					>
						{trigger}
					</Button>
				);
			}

			return trigger({ ...triggerProps, ref: mergedRef, triggerRef: mergedRef });
		},
		[trigger, triggerRef, isOpen, dropdownProps.label, dropdownProps.testId],
	);

	return (
		<>
			{/* eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent -- Preserve existing rendering behavior during the top-layer rollout. */}
			<DropdownMenu
				// eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props -- Preserve the DropdownMenu props supported by this wrapper.
				{...dropdownProps}
				onOpenChange={handleOpenChange}
				trigger={popupTrigger}
			>
				{isAccessibleLoadingEnabled && <MenuBusyState state={contentState} />}
				{children}
			</DropdownMenu>
			{isAccessibleLoadingEnabled && <StatusAnnouncement>{statusText}</StatusAnnouncement>}
		</>
	);
}
